/**
 * Grocery List Confirm View Component
 * 
 * Condensed grocery list view with pantry cross-referencing and color-coded recommendations
 * Items grouped by recipe with undo functionality
 */

import React, { useState, useEffect } from 'react';
import { 
    updateGroceryListItem, 
    deleteRecipeSection, 
    addIndividualItem,
    addRecipeToGroceryList,
    getUserRecipes,
    getAllStackEntries,
    deleteGroceryList
} from '../../services/api';
import { 
    setGroceryListLock, 
    getGroceryListSource 
} from '../../services/unifiedPlanningService';
import { useAuth } from '../../context/authContext';
import Toast from '../Toast';

const colorTokens = {
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a',
    neutral: {
        0: '#FFFFFF',
        50: '#F9F8F6',
        900: '#0D0D0D'
    }
};

const GroceryListConfirmView = ({ list, onUpdate, onAddRecipe, onDelete }) => {
    const { user } = useAuth();
    const [updating, setUpdating] = useState(false);
    const [itemConfirmations, setItemConfirmations] = useState({}); // { index: 'have' | 'dont_have' }
    const [undoState, setUndoState] = useState(null); // { item: {...}, index: number, timeout: number }
    const [groupedItems, setGroupedItems] = useState({}); // { recipeId: [items] }
    const [localList, setLocalList] = useState(list); // Local state for optimistic updates
    const [fadingItems, setFadingItems] = useState(new Set()); // Items that are fading out
    const [showAddItem, setShowAddItem] = useState(false);
    const [showAddRecipe, setShowAddRecipe] = useState(false);
    const [newItem, setNewItem] = useState({ ingredient_name: '', quantity_needed: 0, quantity_to_buy: 0, unit: 'count' });
    const [allRecipes, setAllRecipes] = useState([]);
    const [selectedRecipeToAdd, setSelectedRecipeToAdd] = useState(null);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState('success');
    const [sourceInfo, setSourceInfo] = useState(null);
    const [isLocked, setIsLocked] = useState(false);
    const [lockUpdating, setLockUpdating] = useState(false);

    // Load source info on mount
    useEffect(() => {
        const loadSourceInfo = async () => {
            if (!user || !list?.id) return;
            try {
                const token = await user.getIdToken();
                const info = await getGroceryListSource(list.id, token);
                setSourceInfo(info);
                setIsLocked(info.isLocked || false);
            } catch (err) {
                // Source info is optional, just log and continue
                console.log('Could not load source info:', err.message);
            }
        };
        loadSourceInfo();
    }, [user, list?.id]);

    // Update local list when prop changes
    useEffect(() => {
        setLocalList(list);
    }, [list]);

    // Load recipes for adding
    useEffect(() => {
        const loadRecipes = async () => {
            if (!user) return;
            try {
                const token = await user.getIdToken();
                const userRecipes = await getUserRecipes(user.uid, token);
                const exploreRecipes = await getAllStackEntries();
                setAllRecipes([...userRecipes, ...exploreRecipes]);
            } catch (err) {
                console.error('Error loading recipes:', err);
            }
        };
        if (showAddRecipe) {
            loadRecipes();
        }
    }, [showAddRecipe, user]);

    // Group items by recipe
    useEffect(() => {
        if (!localList || !localList.items) return;
        
        const grouped = {};
        localList.items.forEach((item, index) => {
            if (item.checked || fadingItems.has(index)) return; // Skip checked items and fading items
            
            const recipeIds = item.recipe_sources || [];
            if (recipeIds.length === 0) {
                // If no recipe sources, put in a default group
                if (!grouped['unknown']) {
                    grouped['unknown'] = [];
                }
                grouped['unknown'].push({ ...item, originalIndex: index });
            } else {
                recipeIds.forEach(recipeId => {
                    if (!grouped[recipeId]) {
                        grouped[recipeId] = [];
                    }
                    grouped[recipeId].push({ ...item, originalIndex: index });
                });
            }
        });
        
        setGroupedItems(grouped);
    }, [localList, fadingItems]);

    const handleToggleItem = async (itemIndex, checked) => {
        if (!user || !localList) return;
        
        if (checked) {
            // Item is being checked - mark as checked, fade out, then remove
            const item = localList.items[itemIndex];
            
            // Optimistic update: mark as checked immediately
            const updatedItems = [...localList.items];
            updatedItems[itemIndex] = { ...updatedItems[itemIndex], checked: true };
            setLocalList({ ...localList, items: updatedItems });
            
            // Add to fading items
            setFadingItems(prev => new Set([...prev, itemIndex]));
            
            // Set up undo state
            const timeoutId = setTimeout(() => {
                setUndoState(null);
            }, 4000);
            
            setUndoState({
                item,
                index: itemIndex,
                timeoutId
            });
            
            try {
                setUpdating(true);
                const token = await user.getIdToken();
                
                // Update on server
                await updateGroceryListItem(localList.id, itemIndex, true, token);
                
                // After fade animation (500ms), remove from list
                setTimeout(() => {
                    setFadingItems(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(itemIndex);
                        return newSet;
                    });
                    
                    if (onUpdate) {
                        onUpdate();
                    }
                }, 500);
            } catch (err) {
                console.error('Error updating item:', err);
                // Revert optimistic update on error
                const revertedItems = [...localList.items];
                revertedItems[itemIndex] = { ...revertedItems[itemIndex], checked: false };
                setLocalList({ ...localList, items: revertedItems });
                setFadingItems(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(itemIndex);
                    return newSet;
                });
            } finally {
                setUpdating(false);
            }
        } else {
            // Item is being unchecked - restore it
            try {
                setUpdating(true);
                const token = await user.getIdToken();
                
                // Optimistic update
                const updatedItems = [...localList.items];
                updatedItems[itemIndex] = { ...updatedItems[itemIndex], checked: false };
                setLocalList({ ...localList, items: updatedItems });
                setFadingItems(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(itemIndex);
                    return newSet;
                });
                
                await updateGroceryListItem(localList.id, itemIndex, false, token);
                
                // Clear undo state if this was the undone item
                if (undoState && undoState.index === itemIndex) {
                    if (undoState.timeoutId) {
                        clearTimeout(undoState.timeoutId);
                    }
                    setUndoState(null);
                }
                
                if (onUpdate) {
                    onUpdate();
                }
            } catch (err) {
                console.error('Error updating item:', err);
            } finally {
                setUpdating(false);
            }
        }
    };

    const handleUndo = async () => {
        if (!user || !localList || !undoState) return;
        
        try {
            setUpdating(true);
            const token = await user.getIdToken();
            
            // Optimistic update: uncheck the item immediately
            const updatedItems = [...localList.items];
            updatedItems[undoState.index] = { ...updatedItems[undoState.index], checked: false };
            setLocalList({ ...localList, items: updatedItems });
            setFadingItems(prev => {
                const newSet = new Set(prev);
                newSet.delete(undoState.index);
                return newSet;
            });
            
            // Uncheck the item on server
            await updateGroceryListItem(localList.id, undoState.index, false, token);
            
            // Clear undo state
            if (undoState.timeoutId) {
                clearTimeout(undoState.timeoutId);
            }
            setUndoState(null);
            
            if (onUpdate) {
                onUpdate();
            }
        } catch (err) {
            console.error('Error undoing item removal:', err);
        } finally {
            setUpdating(false);
        }
    };

    const handleConfirmAvailability = (itemIndex, hasItem) => {
        setItemConfirmations(prev => ({
            ...prev,
            [itemIndex]: hasItem ? 'have' : 'dont_have'
        }));
    };

    const getRecommendationColor = (item) => {
        const confidence = item.recommendation_confidence || 'low';
        const reason = item.recommendation_reason || 'not_in_pantry';
        const confirmed = itemConfirmations[list.items.indexOf(item)];
        
        // User confirmation overrides recommendations
        if (confirmed === 'have') {
            return {
                backgroundColor: colorTokens.oxfordBlue,
                color: '#FFFFFF',
                opacity: 1
            };
        } else if (confirmed === 'dont_have') {
            return {
                backgroundColor: colorTokens.neutral[0],
                color: colorTokens.neutral[900],
                opacity: 1,
                borderColor: '#DC2626'
            };
        }
        
        // Color based on recommendation confidence
        switch (confidence) {
            case 'high':
                // High confidence available - Oxford Blue
                if (reason === 'in_pantry' || reason === 'recently_purchased') {
                    return {
                        backgroundColor: colorTokens.oxfordBlue,
                        color: '#FFFFFF',
                        opacity: 1
                    };
                }
                break;
            case 'medium':
                // Medium confidence - Neutral with slight tint
                return {
                    backgroundColor: colorTokens.neutral[50],
                    color: colorTokens.neutral[900],
                    opacity: 0.8
                };
            case 'low':
            default:
                // Low confidence / likely need to buy - Red tint
                return {
                    backgroundColor: colorTokens.neutral[0],
                    color: colorTokens.neutral[900],
                    opacity: 1,
                    borderColor: '#DC2626'
                };
        }
        
        // Default
        return {
            backgroundColor: colorTokens.neutral[0],
            color: colorTokens.neutral[900],
            opacity: 1
        };
    };

    const getRecommendationText = (item) => {
        const confidence = item.recommendation_confidence || 'low';
        const reason = item.recommendation_reason || 'not_in_pantry';
        
        switch (reason) {
            case 'in_pantry':
                return 'Likely have in pantry';
            case 'recently_purchased':
                return 'Recently purchased';
            case 'recently_purchased_but_used':
                return 'Recently purchased, may have been used';
            case 'partial_pantry':
                return 'Have some, need more';
            case 'purchased_but_likely_used':
                return 'Purchased but likely used';
            case 'not_in_pantry':
            default:
                return 'Likely need to buy';
        }
    };

    // Create a map of recipe IDs to names for quick lookup
    const recipeNameMap = React.useMemo(() => {
        const map = new Map();
        if (list.recipe_ids && list.recipeNames && Array.isArray(list.recipe_ids) && Array.isArray(list.recipeNames)) {
            list.recipe_ids.forEach((id, index) => {
                const recipeIdNum = typeof id === 'string' ? parseInt(id) : id;
                if (index < list.recipeNames.length && list.recipeNames[index]) {
                    map.set(recipeIdNum, list.recipeNames[index]);
                }
            });
        }
        return map;
    }, [list.recipe_ids, list.recipeNames]);

    // Get recipe names for display
    const getRecipeName = (recipeId) => {
        if (recipeId === 'unknown') {
            return 'Other Items';
        }
        const recipeIdNum = typeof recipeId === 'string' ? parseInt(recipeId) : recipeId;
        const name = recipeNameMap.get(recipeIdNum);
        if (name) {
            return name;
        }
        return `Recipe ${recipeId}`;
    };

    const handleDeleteSection = async (recipeId) => {
        if (!user || !localList || recipeId === 'unknown') return;
        
        if (!window.confirm(`Delete all items from ${getRecipeName(parseInt(recipeId))}?`)) {
            return;
        }
        
        try {
            setUpdating(true);
            const token = await user.getIdToken();
            const result = await deleteRecipeSection(localList.id, parseInt(recipeId), token);
            setLocalList(result.list);
            if (onUpdate) {
                onUpdate();
            }
        } catch (err) {
            console.error('Error deleting section:', err);
            alert('Failed to delete section. Please try again.');
        } finally {
            setUpdating(false);
        }
    };

    const handleAddItem = async () => {
        if (!user || !localList || !newItem.ingredient_name) return;
        
        try {
            setUpdating(true);
            const token = await user.getIdToken();
            const item = {
                ...newItem,
                quantity_to_buy: newItem.quantity_to_buy || newItem.quantity_needed || 0,
                recipe_sources: [],
                checked: false
            };
            const result = await addIndividualItem(localList.id, item, token);
            setLocalList(result.list);
            setNewItem({ ingredient_name: '', quantity_needed: 0, quantity_to_buy: 0, unit: 'count' });
            setShowAddItem(false);
            if (onUpdate) {
                onUpdate();
            }
        } catch (err) {
            console.error('Error adding item:', err);
            alert('Failed to add item. Please try again.');
        } finally {
            setUpdating(false);
        }
    };

    const handleAddRecipe = async () => {
        if (!user || !localList || !selectedRecipeToAdd) return;
        
        try {
            setUpdating(true);
            const token = await user.getIdToken();
            await addRecipeToGroceryList(selectedRecipeToAdd.id, null, token);
            setSelectedRecipeToAdd(null);
            setShowAddRecipe(false);
            
            // Show success toast
            setToastType('success');
            setToastMessage(`✓ Added "${selectedRecipeToAdd.name || selectedRecipeToAdd.title || 'recipe'}" to grocery list`);
            
            if (onUpdate) {
                onUpdate();
            }
            if (onAddRecipe) {
                onAddRecipe();
            }
        } catch (err) {
            console.error('Error adding recipe:', err);
            setToastType('error');
            setToastMessage('Failed to add recipe. Please try again.');
        } finally {
            setUpdating(false);
        }
    };

    const handleToggleLock = async () => {
        if (!user || !localList) return;
        
        try {
            setLockUpdating(true);
            const token = await user.getIdToken();
            const newLockState = !isLocked;
            await setGroceryListLock(localList.id, newLockState, token);
            setIsLocked(newLockState);
            setToastType('success');
            setToastMessage(newLockState ? '🔒 List locked - regenerating will create a new list' : '🔓 List unlocked');
        } catch (err) {
            console.error('Error toggling lock:', err);
            setToastType('error');
            setToastMessage('Failed to update lock status');
        } finally {
            setLockUpdating(false);
        }
    };

    const handleDeleteList = async () => {
        if (!user || !localList) return;
        
        if (!window.confirm('Are you sure you want to delete this grocery list? This action cannot be undone.')) {
            return;
        }
        
        try {
            setUpdating(true);
            const token = await user.getIdToken();
            await deleteGroceryList(localList.id, token);
            setToastType('success');
            setToastMessage('✓ Grocery list deleted');
            
            if (onDelete) {
                onDelete();
            } else if (onUpdate) {
                onUpdate();
            }
        } catch (err) {
            console.error('Error deleting list:', err);
            setToastType('error');
            setToastMessage('Failed to delete grocery list');
        } finally {
            setUpdating(false);
        }
    };

    // Get "Why is this here?" info for an item
    const getItemSourceInfo = (item) => {
        if (item.meal_sources && item.meal_sources.length > 0) {
            const uniqueDates = [...new Set(item.meal_sources.map(m => 
                new Date(m.scheduled_date).toLocaleDateString()
            ))];
            const uniqueRecipes = [...new Set(item.meal_sources.map(m => m.recipe_name))];
            return {
                dates: uniqueDates,
                recipes: uniqueRecipes,
                description: uniqueRecipes.length === 1 
                    ? `From "${uniqueRecipes[0]}" scheduled for ${uniqueDates.join(', ')}`
                    : `From ${uniqueRecipes.length} recipes across ${uniqueDates.length} day(s)`
            };
        }
        return null;
    };

    if (!localList || !localList.items || localList.items.length === 0) {
        return (
            <div className="bg-neutral-0 p-6 hairline">
                <h3 className="font-sans text-lg font-medium text-neutral-900 text-center mb-6">No items in this list.</h3>
                <div className="flex flex-col md:flex-row gap-6 max-w-3xl mx-auto">
                    <div className="flex-1 space-y-3">
                        <div className="space-y-1.5">
                            <p className="font-mono text-sm uppercase tracking-wider text-neutral-900">STEP 1</p>
                            <p className="font-sans text-base text-neutral-900 opacity-80">
                                Go to a recipe detail page and view the ingredients.
                            </p>
                        </div>
                        <div className="space-y-1.5">
                            <p className="font-mono text-sm uppercase tracking-wider text-neutral-900">STEP 2</p>
                            <p className="font-sans text-base text-neutral-900 opacity-80">
                                Click "Add to Grocery List" to add the recipe's ingredients to your grocery list.
                            </p>
                        </div>
                        <div className="space-y-1.5">
                            <p className="font-mono text-sm uppercase tracking-wider text-neutral-900">STEP 3</p>
                            <p className="font-sans text-base text-neutral-900 opacity-80">
                                Your grocery list will appear here with all the ingredients you need to buy.
                            </p>
                        </div>
                    </div>
                    <div className="flex-1 flex items-center justify-center">
                        <img 
                            src="/images/Grocery-how-to.png" 
                            alt="How to create a grocery list" 
                            className="w-full max-w-sm mx-auto hairline"
                        />
                    </div>
                </div>
            </div>
        );
    }

    // Get all unique recipe IDs from unchecked items
    const recipeIds = Object.keys(groupedItems).filter(recipeId => 
        groupedItems[recipeId] && groupedItems[recipeId].length > 0
    );

    return (
        <>
            {toastMessage && (
                <Toast
                    message={toastMessage}
                    type={toastType}
                    onClose={() => setToastMessage('')}
                    duration={3000}
                />
            )}
            <div className="w-full">
            <div className="bg-neutral-0 p-6 hairline">
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex-1">
                        <h2 className="font-sans text-2xl font-medium text-[#1b263b] mb-1">
                            {localList.occasion_name || localList.name || 'Grocery List'}
                        </h2>
                        {localList.recipeNames && localList.recipeNames.length > 0 && (
                            <p className="font-sans text-xs text-neutral-900 opacity-60">
                                Recipes: {localList.recipeNames.join(', ')}
                            </p>
                        )}
                    </div>
                    <div className="flex gap-2">
                        {/* Lock/Unlock Button */}
                        <button
                            onClick={handleToggleLock}
                            disabled={lockUpdating}
                            className={`px-2 py-1 font-mono text-xs uppercase tracking-wider hairline transition-all flex items-center gap-1 ${
                                isLocked 
                                    ? 'bg-[#1b263b] text-white hover:bg-neutral-900' 
                                    : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0'
                            }`}
                            title={isLocked ? 'List is locked - regenerating creates a new list' : 'Lock list to prevent auto-updates'}
                        >
                            <span>{isLocked ? '🔒' : '🔓'}</span>
                        </button>
                        {/* Delete Button */}
                        <button
                            onClick={handleDeleteList}
                            disabled={updating}
                            className="px-2 py-1 font-mono text-xs uppercase tracking-wider hairline bg-red-50 text-red-900 hover:bg-red-600 hover:text-white transition-all"
                            title="Delete this grocery list"
                        >
                            🗑️
                        </button>
                    </div>
                </div>

                <div className="mb-4 flex gap-2">
                    <button
                        onClick={() => setShowAddRecipe(true)}
                        className="px-4 py-2 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all"
                    >
                        Add Recipe
                    </button>
                    <button
                        onClick={() => setShowAddItem(true)}
                        className="px-4 py-2 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all"
                    >
                        Add Item
                    </button>
                </div>

                <div className="space-y-3">
                {recipeIds.map((recipeId, recipeIndex) => {
                    const items = groupedItems[recipeId] || [];
                    if (items.length === 0) return null;
                    
                    // Use a unique key that includes the index to handle duplicate recipe IDs
                    const recipeKey = `${recipeId}-${recipeIndex}`;
                    
                    return (
                        <div key={recipeKey} className="space-y-1">
                            <div className="flex items-center justify-between mb-1">
                                <h3 className="font-sans text-lg font-medium text-[#1b263b]">
                                    {getRecipeName(parseInt(recipeId))}
                                </h3>
                                {recipeId !== 'unknown' && (
                                    <button
                                        onClick={() => handleDeleteSection(recipeId)}
                                        disabled={updating}
                                        className="px-3 py-1 font-mono text-xs uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-red-600 hover:text-white transition-all"
                                    >
                                        Delete Section
                                    </button>
                                )}
                            </div>
                            {items.map((item, itemIndex) => {
                                const originalIndex = item.originalIndex;
                                const isFading = fadingItems.has(originalIndex);
                                const isChecked = localList.items[originalIndex]?.checked || false;
                                const style = getRecommendationColor(item);
                                const recommendationText = getRecommendationText(item);
                                const confirmed = itemConfirmations[originalIndex];
                                
                                // Use a unique key combining recipeId, originalIndex, and ingredient name
                                const uniqueKey = `${recipeId}-${originalIndex}-${item.ingredient_name}-${item.unit}`;
                                
                                return (
                                    <div
                                        key={uniqueKey}
                                        className={`p-2 hairline flex items-center justify-between transition-all duration-500 ${
                                            isFading ? 'opacity-0' : 'opacity-100'
                                        }`}
                                        style={{
                                            backgroundColor: isChecked ? '#0D0D0D' : style.backgroundColor,
                                            color: isChecked ? '#FFFFFF' : style.color,
                                            opacity: isFading ? 0 : (isChecked ? 0.3 : style.opacity),
                                            borderColor: style.borderColor || undefined,
                                            borderWidth: style.borderColor ? '2px' : '1px',
                                            transform: isFading ? 'translateY(-10px)' : 'translateY(0)'
                                        }}
                                    >
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="font-sans text-base font-medium">
                                                    {item.ingredient_name}
                                                </h3>
                                                <span className="font-mono text-xs uppercase tracking-wider opacity-60">
                                                    {item.quantity_to_buy || item.quantity_needed} {item.unit}
                                                </span>
                                            </div>
                                            
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <span className="font-mono text-xs uppercase tracking-wider opacity-60">
                                                    {recommendationText}
                                                </span>
                                                
                                                {item.stock_quantity > 0 && (
                                                    <span className="font-sans text-xs opacity-60">
                                                        Have: {item.stock_quantity} {item.unit}
                                                    </span>
                                                )}
                                                
                                                {/* "Why is this here?" source info */}
                                                {(() => {
                                                    const itemSource = getItemSourceInfo(item);
                                                    if (itemSource) {
                                                        return (
                                                            <span 
                                                                className="font-sans text-xs opacity-60 cursor-help"
                                                                title={itemSource.description}
                                                            >
                                                                📍 {itemSource.recipes[0]}{itemSource.recipes.length > 1 ? ` +${itemSource.recipes.length - 1}` : ''}
                                                            </span>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-2 ml-4">
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={(e) => handleToggleItem(originalIndex, e.target.checked)}
                                                disabled={updating || isFading}
                                                className="checkbox-label"
                                                style={{ width: '20px', height: '20px' }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
                </div>
            </div>
            
            {/* Add Item Modal */}
            {showAddItem && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-neutral-0 p-6 hairline max-w-md w-full mx-4">
                        <h3 className="font-sans text-xl font-medium text-[#1b263b] mb-4">Add Item</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="font-mono text-xs uppercase tracking-wider text-neutral-900 block mb-1">
                                    Ingredient Name
                                </label>
                                <input
                                    type="text"
                                    value={newItem.ingredient_name}
                                    onChange={(e) => setNewItem({ ...newItem, ingredient_name: e.target.value })}
                                    className="w-full p-2 hairline font-sans text-sm text-neutral-900"
                                    placeholder="e.g., Milk"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="font-mono text-xs uppercase tracking-wider text-neutral-900 block mb-1">
                                        Quantity Needed
                                    </label>
                                    <input
                                        type="number"
                                        value={newItem.quantity_needed}
                                        onChange={(e) => setNewItem({ ...newItem, quantity_needed: parseFloat(e.target.value) || 0 })}
                                        className="w-full p-2 hairline font-sans text-sm text-neutral-900"
                                        min="0"
                                        step="0.1"
                                    />
                                </div>
                                <div>
                                    <label className="font-mono text-xs uppercase tracking-wider text-neutral-900 block mb-1">
                                        Unit
                                    </label>
                                    <select
                                        value={newItem.unit}
                                        onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                                        className="w-full p-2 hairline font-sans text-sm text-neutral-900"
                                    >
                                        <option value="count">count</option>
                                        <option value="cups">cups</option>
                                        <option value="oz">oz</option>
                                        <option value="lbs">lbs</option>
                                        <option value="tbsp">tbsp</option>
                                        <option value="tsp">tsp</option>
                                    </select>
                                </div>
                            </div>
                            <div className="flex gap-2 justify-end">
                                <button
                                    onClick={() => {
                                        setShowAddItem(false);
                                        setNewItem({ ingredient_name: '', quantity_needed: 0, quantity_to_buy: 0, unit: 'count' });
                                    }}
                                    className="px-4 py-2 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddItem}
                                    disabled={!newItem.ingredient_name || updating}
                                    className="px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none"
                                    style={{ backgroundColor: '#1b263b', color: '#FFFFFF' }}
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Recipe Modal */}
            {showAddRecipe && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-neutral-0 p-6 hairline max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
                        <h3 className="font-sans text-xl font-medium text-[#1b263b] mb-4">Add Recipe</h3>
                        <div className="space-y-4">
                            <div className="max-h-64 overflow-y-auto space-y-2">
                                {allRecipes.map((recipe) => (
                                    <button
                                        key={recipe.id}
                                        onClick={() => setSelectedRecipeToAdd(recipe)}
                                        className={`w-full p-3 text-left hairline transition-all ${
                                            selectedRecipeToAdd?.id === recipe.id
                                                ? 'bg-[#1b263b] text-white'
                                                : 'bg-neutral-50 text-neutral-900 hover:bg-neutral-900 hover:text-neutral-0'
                                        }`}
                                    >
                                        <div className="font-sans font-medium">
                                            {recipe.name || recipe.title}
                                        </div>
                                    </button>
                                ))}
                            </div>
                            <div className="flex gap-2 justify-end">
                                <button
                                    onClick={() => {
                                        setShowAddRecipe(false);
                                        setSelectedRecipeToAdd(null);
                                    }}
                                    className="px-4 py-2 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddRecipe}
                                    disabled={!selectedRecipeToAdd || updating}
                                    className="px-4 py-2 font-mono text-sm uppercase tracking-wider transition-all border-none"
                                    style={{ backgroundColor: '#1b263b', color: '#FFFFFF' }}
                                >
                                    Add Recipe
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Undo Popup */}
            {undoState && (
                <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-[#1b263b] text-white px-6 py-4 font-mono text-sm uppercase tracking-wider z-50 shadow-lg flex items-center gap-4">
                    <span>You already have {undoState.item.ingredient_name}</span>
                    <button
                        onClick={handleUndo}
                        className="px-4 py-2 bg-white text-[#1b263b] hover:opacity-80 transition-opacity border-none font-mono text-sm uppercase tracking-wider"
                    >
                        UNDO
                    </button>
                </div>
            )}
        </div>
        </>
    );
};

export default GroceryListConfirmView;

